import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO, ASSETS } from "@/data/content";
import { MaskLine } from "@/lib/reveal";

// Watercolour community, standing together on the land, a People's Almanac.
const LINEUP = [
 { src: "wcWalking", h: "h-[34vh]", ml: "" },
 { src: "wcRedCap", h: "h-[40vh]", ml: "-ml-8" },
 { src: "wcWatch", h: "h-[37vh]", ml: "-ml-10" },
 { src: "wcGrass", h: "h-[45vh]", ml: "-ml-10" },
 { src: "wcSmile", h: "h-[38vh]", ml: "-ml-10" },
];

export const Hero = () => {
 const ref = useRef(null);
 const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
 const rise = useTransform(scrollYProgress, [0, 1], [0, -70]);

 return (
 <section id="top" ref={ref} className="grain relative min-h-[100svh] overflow-hidden bg-cream pt-28 md:pt-32">
 {/* faint landscape, background texture only */}
 <img src={ASSETS.fenceline} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.10]" />
 <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-cream/30 to-cream" />

 {/* Title block */}
 <div className="relative z-20 mx-auto max-w-[1400px] px-5 md:px-10">
 <div className="max-w-[680px]">
 <MaskLine delay={0.1}>
 <span className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-clay bg-paper/85 px-4 py-1.5 font-body text-xs uppercase tracking-[0.26em] text-ink/70">
 <span className="h-2 w-2 rounded-full bg-carbon" /> {HERO.eyebrow}
 </span>
 </MaskLine>

 <h1 className="font-display text-[12.5vw] font-medium leading-[0.9] tracking-[-0.02em] text-ink sm:text-6xl md:text-7xl lg:text-[5.1rem]">
 {HERO.title.map((line, i) => (
 <MaskLine key={i} delay={0.18 + i * 0.1}>{line}</MaskLine>
 ))}
 <MaskLine delay={0.18 + HERO.title.length * 0.1}>
 <span className="font-script text-terracotta text-[1.15em] italic">{HERO.titleTail}</span>
 </MaskLine>
 </h1>

 <p className="mt-6 max-w-lg font-body text-base text-ink/80 md:text-lg">{HERO.sub}</p>

 <div className="mt-8 flex flex-wrap items-center gap-3">
 <a href="#reserve" data-testid="hero-reserve-btn"
 className="group inline-flex items-center gap-2 rounded-full bg-carbon px-7 py-3.5 font-body text-sm font-medium text-white shadow-[0_10px_30px_rgba(23,165,110,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-carbondark">
 {HERO.ctaPrimary}
 <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
 </a>
 <a href="#newsletter" data-testid="hero-notify-btn"
 className="inline-flex items-center gap-2 rounded-full border border-ink/30 px-7 py-3.5 font-body text-sm font-medium text-ink transition-colors duration-300 hover:border-ink hover:bg-paper">
 {HERO.ctaSecondary}
 </a>
 </div>

 <p className="mt-6 font-script text-2xl text-sage"> a Carbon8 project, put down in ink.</p>
 </div>
 </div>

 {/* Ground line */}
 <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[16vh] bg-gradient-to-t from-sage/25 to-transparent" />

 {/* Community lineup, the FEATURE (desktop) */}
 <motion.div style={{ y: rise }} className="pointer-events-none absolute bottom-0 right-0 z-10 hidden items-end justify-end pr-4 lg:flex xl:pr-10">
 {LINEUP.map((f, i) => (
 <motion.img
 key={f.src}
 src={ASSETS[f.src]}
 alt="A member of the Carbon8 community, watercolour"
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.7 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
 className={`${f.h} ${f.ml} w-auto object-contain drop-shadow-[0_20px_28px_rgba(42,32,21,0.22)]`}
 />
 ))}
 </motion.div>

 {/* Community cluster (mobile / tablet) */}
 <div className="relative z-10 mt-8 flex items-end justify-center lg:hidden">
 {["wcRedCap", "wcGrass", "wcSmile"].map((s, i) => (
 <img key={s} src={ASSETS[s]} alt="Carbon8 community, watercolour"
 className={`${i === 1 ? "h-[34vh]" : "h-[28vh]"} ${i ? "-ml-6" : ""} w-auto object-contain drop-shadow-[0_16px_24px_rgba(42,32,21,0.2)]`} />
 ))}
 </div>

 <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-1.5 text-ink/45 lg:flex">
 <span className="font-body text-[10px] uppercase tracking-[0.3em]">Scroll</span>
 <ArrowDown size={16} className="animate-floaty" />
 </div>
 </section>
 );
};
